import React, { useState } from 'react';
import { ThumbnailCard } from './ThumbnailCard';
import { ModalWindow } from './ModalWindow';
import { ErrorBoundary } from './ErrorBoundary';

interface EmbedWidgetProps {
  exhibitorSlug: string;
  librarySlug: string;
  className?: string;
  onError?: (error: Error) => void;
  href?: string;
  mode?: 'modal' | 'link';
}

export const EmbedWidget: React.FC<EmbedWidgetProps> = ({
  exhibitorSlug,
  librarySlug,
  className = '',
  onError,
  href,
  mode = 'modal'
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isLinkMode = mode === 'link' && !!href;

  const handleError = (error: Error) => {
    console.error('EmbedWidget error:', error);
    onError?.(error);
  };

  const handleThumbnailClick = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  return (
    <ErrorBoundary onError={handleError}>
      <div className={`embed-widget ${className}`}>
        <ThumbnailCard
          exhibitorSlug={exhibitorSlug}
          librarySlug={librarySlug}
          onClick={isLinkMode ? undefined : handleThumbnailClick}
          href={href}
          mode={isLinkMode ? 'link' : 'modal'}
        />
        {!isLinkMode && (
          <ModalWindow
            isOpen={isModalOpen}
            onClose={handleModalClose}
            exhibitorSlug={exhibitorSlug}
            librarySlug={librarySlug}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

interface ThumbnailOptions {
  exhibitorSlug: string;
  librarySlug: string;
  href?: string;
  mode?: 'modal' | 'link';
}

export const extractEmbedOptions = (element: HTMLElement): ThumbnailOptions => {
  const getAttribute = (name: string): string => {
    return element.dataset[name] || '';
  };

  const exhibitorSlug = getAttribute('exhibitorSlug');
  const librarySlug = getAttribute('librarySlug');
  const rawHref = element.dataset.href?.trim();
  const href = rawHref ? rawHref : undefined;
  const modeAttr = element.dataset.mode?.trim().toLowerCase();
  const mode: 'modal' | 'link' = modeAttr === 'link' ? 'link' : 'modal';
  
  if (!exhibitorSlug || !librarySlug) {
    throw new Error('data-exhibitor-slug and data-library-slug are required attributes');
  }
  
  return {
    exhibitorSlug,
    librarySlug,
    href,
    mode
  };
};
